import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { clientStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/styles.ts';
import { AppDispatch, RootState } from '~/store/store.ts';
import {
  fetchCallCenterOperatorContragents,
  fetchMyContragents,
} from '~/store/covenants/thunks/contragentThunks.ts';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { errorMessages } from '~/constants/errorMessages.js';
import type { TApiContragent } from '~/modules/covenants/types.ts';
import { useUserAuthToken } from '~/modules/covenants/hooks/useUserAuthToken.ts';
import useCounterpartySelectorForm, {
  ICounterpartySearchFormValues,
} from '../hooks/useCounterpartySelectorForm.ts';
import {
  buildCachedSearchOptions,
  buildCounterpartyBlackListPath,
  buildCounterpartyDetailsPath,
  buildCounterpartyCacheStorageKey,
  getCachedCounterpartySearch,
  normalizeSearchCode,
  saveCounterpartySearchToCache,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/utils.ts';
import {
  COUNTERPARTY_CACHE_DATE_FORMAT,
  COUNTERPARTY_SELECTOR_DEFAULT_MODE,
  counterpartySearchInputProps,
  counterpartySelectorPaperStyle,
  counterpartySelectorText,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/constants.ts';
import {
  TCounterpartySearchCachePayload,
  TCounterpartySelectorMode,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/types.ts';

interface ICounterpartySelectorProps {
  mode?: TCounterpartySelectorMode;
}

const CounterpartySelector: React.FC<ICounterpartySelectorProps> = ({
  mode = COUNTERPARTY_SELECTOR_DEFAULT_MODE,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const token = useUserAuthToken();
  const userName = useSelector((state: RootState) => state.users.user.username);
  const isOperatorMode = [
    TCounterpartySelectorMode.Operator,
    TCounterpartySelectorMode.CovenantsBranchManager,
    TCounterpartySelectorMode.UakdoAdministrator,
  ].includes(mode);
  const hasSupervisorSearch = isOperatorMode || mode === TCounterpartySelectorMode.EmailBlacklist;
  const buildDestinationPath = (contragentId?: number) =>
    mode === TCounterpartySelectorMode.EmailBlacklist
      ? buildCounterpartyBlackListPath(contragentId)
      : buildCounterpartyDetailsPath(contragentId);
  const [contragents, setContragents] = useState<TApiContragent[]>([]);
  const [isContragentsModalOpen, setIsContragentsModalOpen] = useState(false);
  const [isModalAlertOpen, setIsModalAlertOpen] = useState(false);
  const [modalAlertText, setModalAlertText] = useState('');
  const [cachedSearch, setCachedSearch] = useState<TCounterpartySearchCachePayload | null>(null);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [searchCodeInputValue, setSearchCodeInputValue] = useState('');

  const formMethods = useCounterpartySelectorForm();
  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { isSubmitting },
  } = formMethods;

  const cacheStorageKey = useMemo(
    () =>
      buildCounterpartyCacheStorageKey({
        mode,
        userName: userName || '',
        token,
      }),
    [userName, mode, token]
  );

  const cachedSearchOptions = useMemo(
    () =>
      buildCachedSearchOptions({
        cachedSearch,
        dateFormat: COUNTERPARTY_CACHE_DATE_FORMAT,
        datePrefix: counterpartySelectorText.cachedSearchDropdownDatePrefix,
      }),
    [cachedSearch]
  );
  const normalizedSearchCodeInput = searchCodeInputValue.trim().toLowerCase();
  const visibleCachedSearchOptions = useMemo(() => {
    if (!normalizedSearchCodeInput) {
      return cachedSearchOptions;
    }

    return cachedSearchOptions.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearchCodeInput)
    );
  }, [cachedSearchOptions, normalizedSearchCodeInput]);
  const hasVisibleCachedSearchOptions = visibleCachedSearchOptions.length > 0;

  useEffect(() => {
    const parsedCache = getCachedCounterpartySearch(cacheStorageKey);
    setCachedSearch(parsedCache);
    setSearchCodeInputValue('');

    setValue('searchCode', null, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [cacheStorageKey, setValue]);

  const handleCloseAlertModal = () => setIsModalAlertOpen(false);
  const handleCloseContragentsModal = () => setIsContragentsModalOpen(false);

  const handleSearch = async ({ searchCode }: ICounterpartySearchFormValues) => {
    const normalizedCode = normalizeSearchCode(searchCode);
    setContragents([]);
    setIsContragentsModalOpen(false);

    try {
      const foundContragents = hasSupervisorSearch
        ? await dispatch(
            fetchCallCenterOperatorContragents({
              code: normalizedCode,
            })
          ).unwrap()
        : await dispatch(fetchMyContragents({ code: normalizedCode })).unwrap();

      if (!foundContragents.length) {
        setModalAlertText(counterpartySelectorText.noContragentsFound);
        setIsModalAlertOpen(true);
        return;
      }

      const cachePayload = saveCounterpartySearchToCache(cacheStorageKey, normalizedCode);

      if (cachePayload) {
        setCachedSearch(cachePayload);
      }

      if (foundContragents.length === 1) {
        navigate(buildDestinationPath(foundContragents[0]?.id));
        return;
      }

      setContragents(foundContragents);
      setIsContragentsModalOpen(true);
    } catch (error) {
      if ((error as { status?: number } | null)?.status === 403) {
        setModalAlertText(counterpartySelectorText.accessDenied);
        setIsModalAlertOpen(true);
        return;
      }

      setModalAlertText(errorMessages.SERVICE_UNAVAILABLE);
      setIsModalAlertOpen(true);
    }
  };

  const handleSelectContragent = (contragentId: number) => {
    setIsContragentsModalOpen(false);
    navigate(buildDestinationPath(contragentId));
  };

  const handleSearchFormSubmit = handleSubmit(handleSearch, () => {
    setContragents([]);
    setIsContragentsModalOpen(false);
  });

  const handleSearchCodeInput = (event: React.FormEvent<HTMLInputElement>) => {
    const newInputValue = event.currentTarget.value;
    setSearchCodeInputValue(newInputValue);
    clearErrors('searchCode');
    setValue('searchCode', newInputValue ? { label: newInputValue, value: '-1' } : null, {
      shouldValidate: false,
      shouldDirty: true,
    });
  };
  const handleAutocompleteOpen = () => hasVisibleCachedSearchOptions && setIsAutocompleteOpen(true);

  return (
    <Box sx={clientStyles.selectorWrap}>
      <Paper elevation={3} sx={clientStyles.selectorCard} style={counterpartySelectorPaperStyle}>
        <Typography variant="h5" gutterBottom sx={clientStyles.selectorTitle}>
          {counterpartySelectorText.title}
        </Typography>

        <FormProvider {...formMethods}>
          <Box component="form" onSubmit={handleSearchFormSubmit} sx={clientStyles.selectorForm}>
            <FormAutocomplete
              name="searchCode"
              options={visibleCachedSearchOptions}
              disabled={isSubmitting}
              shouldSaveLabel
              open={hasVisibleCachedSearchOptions && isAutocompleteOpen}
              onOpen={handleAutocompleteOpen}
              onClose={() => setIsAutocompleteOpen(false)}
              filterOptions={(options) => options}
              inputProps={{
                ...counterpartySearchInputProps,
                placeholder: counterpartySelectorText.searchPlaceholder,
                'aria-label': counterpartySelectorText.searchAriaLabel,
                onInput: handleSearchCodeInput,
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={clientStyles.selectorSubmit}
            >
              {isSubmitting
                ? counterpartySelectorText.submitButtonLoading
                : counterpartySelectorText.submitButton}
            </Button>
          </Box>
        </FormProvider>
      </Paper>

      <Dialog
        open={isContragentsModalOpen}
        onClose={handleCloseContragentsModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{counterpartySelectorText.selectionDialogTitle}</DialogTitle>
        <DialogContent>
          <List disablePadding>
            {contragents.map((contragent) => (
              <ListItemButton
                key={contragent.id}
                onClick={() => handleSelectContragent(contragent.id)}
              >
                <ListItemText
                  primary={contragent.displayName}
                  secondary={`${counterpartySelectorText.edrpouLabel}: ${contragent.code}`}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {isModalAlertOpen && (
        <ModalAlert
          isOpen={isModalAlertOpen}
          text={modalAlertText}
          onClose={handleCloseAlertModal}
        />
      )}
    </Box>
  );
};

export default CounterpartySelector;
